import React, { useEffect, useState, useMemo } from "react";

import { logService } from "/app/src/services";
import Status from "./status";
import { useTranslation } from "react-i18next";
import { Log as LogType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import { ArrowCircleRight } from "/app/src/components/icons/icons";
import { useNavigate } from "react-router-dom";
/**
 * Shows the logs for a workflow run
 * @param param object containing workflowHistoryId
 */
export default function ActionLogs({
  workflowHistoryId,
}: {
  workflowHistoryId: number | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const columnHelper = createColumnHelper<LogType>();
  const columns = useMemo(() => {
    /**
     * Handles the click event for navigating to a specific report's export page.
     * @param {number} id - The unique identifier for the report.
     * @returns {Function} A function that navigates to the report's export page.
     */
    const handleClick = (id: number) => {
      return () => navigate(`/reports/${id}/export`);
    };

    /**
     * Parses a JSON string and returns the corresponding object.
     *
     * @param {string} description - The JSON string to parse.
     * @returns {obj | null} The parsed object if the JSON string is valid, otherwise `null`.
     */
    const parseDescription = (description: string) => {
      try {
        return JSON.parse(description);
      } catch {
        // Some logs appear to have a stray trailing quote. Try trimming once.
        const trimmed = description.trim();
        if (trimmed.endsWith('"') && trimmed.length > 2) {
          try {
            return JSON.parse(trimmed.slice(0, -1));
          } catch {
            return null;
          }
        }
        return null;
      }
    };

    /**
     * Dynamically displays the email log details from the parsed description.
     *
     * @param {string} parsedDescription - The JSON string to display.
     */
    const renderEmailDescription = (parsedDescription) => {
      const messageContainer = parsedDescription.message;
      let messageObj = null;
      if (messageContainer) {
        if (typeof messageContainer === "object") {
          messageObj = messageContainer;
        } else if (typeof messageContainer === "string") {
          try {
            const nested = JSON.parse(messageContainer);
            if (nested && typeof nested === "object") {
              messageObj = nested;
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
      const subject = parsedDescription.subject;
      const recipientsCsv = parsedDescription.recipients;
      const fileIds = parsedDescription.file_ids;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {fileIds && Array.isArray(fileIds) && fileIds.length > 0 && (
            <div>
              <strong>{t("translation:email_file_ids_label")}:</strong>{" "}
              {fileIds.join(", ")}
            </div>
          )}
          {subject && (
            <div>
              <strong>{t("translation:email_subject_label")}:</strong> {subject}
            </div>
          )}
          {recipientsCsv && (
            <div>
              <strong>{t("translation:email_recipients_label")}:</strong>{" "}
              {recipientsCsv}
            </div>
          )}
          {messageObj && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2px" }}
            >
              <strong>{t("translation:email_api_details_label")}</strong>
              {messageObj.id && (
                <div>
                  {t("translation:email_api_id_label")}: {messageObj.id}
                </div>
              )}
              {messageObj.message && (
                <div>
                  {t("translation:email_api_result_label")}:{" "}
                  {messageObj.message}
                </div>
              )}
            </div>
          )}
        </div>
      );
    };

    const baseColumns = [
      columnHelper.accessor("status", {
        id: "status",
        cell: (info) => <Status value={info.getValue()} />,
        header: t("translation:status"),
      }),
      columnHelper.accessor("appName", {
        id: "appName",
        cell: (info) => info.getValue(),
        header: t("translation:action"),
      }),
      columnHelper.accessor("description", {
        id: "description",
        cell: (info) => {
          const description = info.getValue();
          const parsedDescription = parseDescription(description);
          // If this is an Email app log, render structured email info.
          const appName = info.row.original.appName;
          if (appName === "Email" && parsedDescription) {
            return renderEmailDescription(parsedDescription);
          }
          // Fallback: if message is an object show its 'message' property if present.
          if (parsedDescription?.message) {
            if (typeof parsedDescription.message === "object") {
              return (
                parsedDescription.message.message ||
                JSON.stringify(parsedDescription.message)
              );
            }
            return parsedDescription.message;
          }
          return description;
        },
        header: t("translation:description"),
      }),
      columnHelper.accessor("error", {
        id: "error",
        cell: (info) => info.getValue(),
        header: t("translation:error"),
      }),
      columnHelper.accessor("description", {
        id: "reportId",
        cell: (info) => {
          const description = info.getValue();
          const parsedDescription = parseDescription(description);
          if (parsedDescription?.reportId) {
            return (
              <button
                onClick={handleClick(parsedDescription.reportId)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <ArrowCircleRight color="green" />
              </button>
            );
          }
          return "";
        },
        header: "",
        meta: {
          sortable: false,
        },
      }),
    ];
    return baseColumns;
  }, [columnHelper, navigate, t]);

  const [sort, setSort] = useSortUpgrade([]);
  const notFoundText = t("translation:logs_not_found_workflow_history");
  const notSelectedText = t("translation:workflow_record_not_selected");
  const [emptyText, setEmptyText] = useState(notSelectedText);

  useEffect(() => {
    if (workflowHistoryId) {
      setEmptyText(notFoundText);
    }
  }, [workflowHistoryId, notFoundText]);

  const {
    data: logs,
    count: logsCount,
    isFetching,
  } = usePaginatedData<LogType>({
    queryKey: ["logs"],
    sort,
    page,
    pageSize,
    service: logService,
    options: { workflowHistoryId },
  });

  return (
    <div className="workflow">
      <Table
        loading={isFetching}
        rows={logs}
        tableColumns={columns}
        length={logsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage,
          setPageSize,
        }}
        enableRowSelection={false}
        emptyText={emptyText}
      />
    </div>
  );
}
